import axios from 'axios';
import BaseApi from './BaseApi';
import { BaseJsonResponse } from 'src/Types';
import { FileAttachmentResponseType } from 'src/Types/File/Attachment';
import {
	CreateFileResponseType,
	ICommonInfoResponse,
	IEditCommonInfoRequestType,
	FileArchivationConfigType,
	ComponentListType,
	LogRecordType,
	CreateCommentResponse,
	CommentRequestBodyType,
	GetCommentsRequestBodyType,
	ICommentPave,
} from 'src/Types/File';

type getAttachmentsType = (id: string) => Promise<FileAttachmentResponseType>;

const API = {
	/**
	 * Получение полной информации о деле (File) для отображения страницы просмотра.
	 * Возвращает вложения, задачи, общую информацию, участников и другое
	 */
	FILE: '/WebInterfaceModule/File/',
};

export default class File extends BaseApi {
	/** */
	static getAttachments: getAttachmentsType = async (id) => {
		const url = window.host + '/files/' + id + '/attachments';
		try {
			const request = await fetch(url, { credentials: 'include' });
			if (this.checkUnauthorized(request)) {
				throw new Error(
					'Ошибка при получении списка вложений. Авторизационные данные устарели',
				);
			}
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error(
				'Ошибка при получении списка вложений. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			return {
				hasError: true,
				errorMessage: error.message,
				result: [],
			};
		}
	};

	/** */
	static create = async (
		templateId?: string,
	): Promise<CreateFileResponseType> => {
		const url =
			window.host +
			'/WebInterfaceModule/File/New' +
			(templateId ? '?templateId=' + templateId : '');
		try {
			const request = await fetch(url, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
			});
			if (request.status === 200) {
				return await request.json();
			}
			throw new Error(
				'Ошибка при создании дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static changeCommonInfo = async (
		requestBody: IEditCommonInfoRequestType,
	): Promise<ICommonInfoResponse> => {
		const url = window.host + '/WebInterfaceModule/File/SaveCommonInfo';
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при создании дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/**  */
	static changePublicAccess = async (
		fileId: string,
		access: boolean,
	): Promise<CreateFileResponseType> => {
		const url =
			window.host +
			API.FILE +
			(access ? 'Public?fileId=' : 'UnPublic?fileId=') +
			fileId;
		try {
			const request = await axios.post(url, undefined, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при изменении доступности дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/**  */
	static removeParticipant = async (
		fileId: string,
		participantId: string,
	): Promise<CreateFileResponseType> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/FileController/RemoveParticipantJson';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('participantId', participantId);
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при удалении участника дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/**  */
	static addParticipant = async (
		fileId: string,
		participantId: string,
	): Promise<CreateFileResponseType> => {
		const url = window.host + '/WebInterfaceModule/File/AddParticipant';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('participantId', participantId);
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при добавлении участника дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static checkFileArchived = async (
		fileId: string,
	): Promise<CreateFileResponseType> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/FileController/IsFileArchived';
		const requestBody = { fileId };
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return {
					...request.data,
					hasError: !request.data.hasError,
				};
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при создании дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static unarchiveFile = async (
		fileId: string,
	): Promise<CreateFileResponseType> => {
		const url = window.host + '/WebInterfaceModule/File/UnarchiveFile';
		const requestBody = { fileId };
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при разархивации дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static archiveFile = async (
		fileId: string,
		reason?: string,
	): Promise<CreateFileResponseType> => {
		const url =
			window.host +
			'/WebInterfaceModule/File/Archive?fileId=' +
			fileId +
			(reason ? '&reason=' + reason : '');
		const requestBody = { fileId };
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при архивации дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	static getFileArchivationConfig =
		async (): Promise<FileArchivationConfigType | null> => {
			const url =
				window.host +
				'/WebInterfaceModule/Controller/FileController/GetFileArchivationConfig';
			try {
				const request = await axios.get(url, { withCredentials: true });
				if ([200, 403].includes(request.status)) {
					return request.data;
				}
				throw new Error(
					'Ошибка при получении настроек архивации дела. Сервер вернул - ' +
						request.statusText,
				);
			} catch (error: any) {
				return null;
			}
		};

	/** */
	static setAsTest = async (
		fileId: string,
		isTest: boolean = true,
	): Promise<CreateFileResponseType> => {
		const url =
			window.host +
			API.FILE +
			(isTest ? 'SetAsTest?fileId=' : 'UnsetTest?fileId=') +
			fileId;
		const requestBody = { fileId };
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при разархивации дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static getChanges = async (
		fileId: string,
		take: number = 20,
		skip: number = 0,
	): Promise<ComponentListType<LogRecordType>> => {
		const url =
			window.host +
			'/WebInterfaceModule/File/ChangesJson?fileId=' +
			fileId +
			'&skip=' +
			skip +
			'&take=' +
			take;
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (request.status === 200) {
				return request.data;
			}
			throw new Error(
				'Ошибка при получении истории дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
					components: [],
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
				components: [],
			};
		}
	};

	/** */
	static getComments = async (
		fileId: string,
		take: number = 25,
		skip: number = 0,
	): Promise<ComponentListType<ICommentPave>> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/FileController/FileCommentsJson';
		const body: GetCommentsRequestBodyType = { fileId, take, skip };
		try {
			const request = await axios.post(url, body, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return {
					...request.data,
					hasError: false,
					errorMessage: '',
				};
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
					components: [],
				};
			}
			throw new Error(
				'Ошибка при добавлении участника дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
				components: [],
			};
		}
	};

	/** */
	static saveComment = async (
		data: CommentRequestBodyType,
	): Promise<CreateCommentResponse> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/FileController/SaveComment';
		try {
			const request = await axios.post(url, data, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return {
					...request.data,
					hasError: false,
					errorMessage: '',
				};
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при добавлении участника дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static removeComment = async (
		fileId: string,
		commentId: string,
	): Promise<BaseJsonResponse> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/FileController/DelComment';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('commentId', commentId);
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return {
					...request.data,
					hasError: false,
					errorMessage: '',
				};
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при добавлении участника дела. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static removeAttachment = async (
		fileId: string,
		attachmentId: string,
	): Promise<BaseJsonResponse> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/AttachmentController/DeleteAttachment';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('attachmentId', attachmentId);
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при удалении вложения. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static removeSignRequest = async (
		fileId: string,
		attachmentId: string,
		userId: string,
	): Promise<BaseJsonResponse> => {
		const url =
			window.host +
			'/WebInterfaceModule/Attachment/RemoveRequestSignature';
		const frmData = { fileId, attachmentId, userId };
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return {
					...request.data,
					hasError: false,
					errorMessage: '',
				};
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка при запроса на подпись. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** Удаление подписи файла */
	static removeSign = async (
		fileId: string,
		attachmentId: string,
		userId: string,
		reason: string,
	): Promise<BaseJsonResponse> => {
		const url =
			window.host +
			'/Nvx.ReDoc.WebInterfaceModule/Controller/AttachmentController/RemoveSignature';
		const requestBody = { fileId, userId, attachmentId, reason };
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return {
					hasError: false,
					errorMessage: '',
					...request.data,
				};
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка запроса на удаление подписи. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};

	/** */
	static requestSign = async (
		fileId: string,
		attachmentId: string,
		userId: string,
	): Promise<BaseJsonResponse> => {
		const url =
			window.host + '/WebInterfaceModule/Attachment/RequestSignature';
		const requestBody = { fileId, userId, attachmentId };
		try {
			const request = await axios.post(url, requestBody, {
				withCredentials: true,
			});
			if (request.status === 200) {
				return request.data;
			} else if (request.status === 403) {
				return {
					errorMessage: request.data.errorMessage,
					hasError: true,
				};
			}
			throw new Error(
				'Ошибка запроса на удаление подписи. Сервер вернул - ' +
					request.statusText,
			);
		} catch (error: any) {
			if (error.response.data) {
				return {
					hasError: true,
					...error.response.data,
				};
			}
			return {
				hasError: true,
				errorMessage: error.message,
			};
		}
	};
}
